import { useEffect, useState } from 'react'
import { auth, users, referrals, settings } from '@/api/endpoints'
import { useAuthStore } from '@/store/authStore'
import { useSettingsStore } from '@/store/settingsStore'
import { useTelegram } from './useTelegram'

const REFERRAL_APPLIED_KEY = 'sr_referral_applied'

async function tryApplyReferralCode() {
  // Only attempt once per device — backend also enforces one-per-user,
  // but this avoids a pointless network call on every subsequent launch.
  if (localStorage.getItem(REFERRAL_APPLIED_KEY)){
    console.log('referral applied from local storage already');
    
    return}

  const tg = (window as { Telegram?: { WebApp?: { initDataUnsafe?: { start_param?: string } } } })
    .Telegram?.WebApp

  // Primary: SDK's initDataUnsafe.start_param — populated when opened via a t.me deep link.
  // Fallback: window.location.search ?startapp= — populated when the bot opens the mini app
  //           via an inline webApp button with the code appended directly to the URL.
  const startParam =
    tg?.initDataUnsafe?.start_param ??
    new URLSearchParams(window.location.search).get('startapp') ??
    undefined

  console.log('[Referral] startParam:', startParam ?? '(none)', '| source:', tg?.initDataUnsafe?.start_param ? 'initDataUnsafe' : 'locationSearch')

  // No referral code in this session — exit WITHOUT setting the flag so that
  // if this user later opens from a referral link, we still try to apply it.
  if (!startParam || !startParam.startsWith('SPIN-')) {
    console.log('[Referral] No valid SPIN- code found, skipping')
    return
  }

  // A code is present — attempt it. Set the flag in `finally` so we never
  // retry even if the call errors (backend enforces one-per-user anyway).
  try {
    await referrals.apply(startParam)
    console.log('[Referral] Code applied:', startParam)
  } catch {
    // Silently ignore — backend returns 400 if code already used,
    // user applied their own code, or they already have a referral.
  } finally {
    localStorage.setItem(REFERRAL_APPLIED_KEY, 'true')
  }
}

export function useAuth() {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  useTelegram() // still call it so WebApp.ready() / WebApp.expand() fires
  const { setAuth, setTokens, clearAuth, isAuthenticated, tokens, user } =
    useAuthStore()
  const { setSettings } = useSettingsStore()

  useEffect(() => {
    // cancelled flag prevents the first StrictMode effect invocation from
    // setting state after React has already re-fired the effect a second time
    let cancelled = false

    async function authenticate() {
      // Diagnostic: dump full WebApp state before any polling
      const tgDebug = (window as any).Telegram?.WebApp
      console.log('[Auth] WebApp diagnostic:', JSON.stringify({
        hasTelegram: !!(window as any).Telegram,
        hasWebApp: !!tgDebug,
        version: tgDebug?.version,
        platform: tgDebug?.platform,
        initData: tgDebug?.initData,
        initDataLength: tgDebug?.initData?.length,
        initDataUnsafe: tgDebug?.initDataUnsafe,
        hasProxy: !!(window as any).TelegramWebviewProxy,
        hashLength: window.location.hash?.length,
        hasTgWebAppData: window.location.hash?.includes('tgWebAppData'),
        locationSearch: window.location.search?.slice(0, 200),
        referrer: document.referrer?.slice(0, 100),
      }))

      try {
        // If we have a stored token, try to fetch the user profile directly
        if (tokens?.access) {
          try {
            const me = await users.me()
            if (cancelled) return
            setAuth(me, tokens)
            // Bootstrap public settings for returning users too (fire-and-forget)
            settings.public().then(setSettings).catch(() => { /* use defaults */ })
            setIsLoading(false)
            return
          } catch {
            if (cancelled) return
            // Token expired or invalid — fall through to re-auth
            clearAuth()
          }
        }

        // initData arrives asynchronously via postMessage from Telegram's shell
        // after WebApp.ready() is called. Poll for up to 1 second so we don't
        // read it before Telegram has had a chance to inject it.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
       const getInitData = async (): Promise<string | undefined> => {
  const tg = (window as any).Telegram?.WebApp;

  if (tg) {
    // Notify Telegram the app is ready
    tg.ready();
    
    // If data is already there, return it
    if (tg.initData) return tg.initData;
  }

  // Fallback: If you still need a slight wait for async script injection
  for (let i = 0; i < 10; i++) {
    await new Promise((r) => setTimeout(r, 100));
    const liveData = (window as any).Telegram?.WebApp?.initData;
    if (liveData) return liveData;
  }

  return undefined;
};


        const devInitData = import.meta.env.DEV
          ? (import.meta.env.VITE_DEV_INIT_DATA as string | undefined)
          : undefined
        const activeInitData = await getInitData() || devInitData

        console.log('[Auth] initData present:', !!activeInitData, '| length:', activeInitData?.length ?? 0)

        if (!activeInitData) {
          if (!cancelled) {
            setError('No Telegram context. Open this app inside Telegram.')
            setIsLoading(false)
          }
          return
        }

        if (!activeInitData && devInitData) {
          console.warn('[Auth] Using VITE_DEV_INIT_DATA — dev only')
        }

        const { user: authUser, tokens: authTokens } =
          await auth.telegram(activeInitData)
        if (cancelled) return
        setAuth(authUser, authTokens)
        setTokens(authTokens)

        // Bootstrap public settings (fire-and-forget — non-blocking)
        settings.public().then(setSettings).catch(() => { /* use defaults if unavailable */ })

        // Fresh login — attempt to apply a referral code from the deep link.
        // Fire-and-forget: we don't await so it never delays the app loading.
        console.log('attempting to apply referral code');

        tryApplyReferralCode()
      } catch (err) {
        console.error('[Auth] Failed:', err)
        if (!cancelled) {
          setError('Authentication failed. Please try again.')
          clearAuth()
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    authenticate()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { isLoading, error, isAuthenticated, user }
}
